import { useCallback } from 'react';
import { pick, prop } from 'ramda';
import { get } from 'js/utils/fetch.util';
import { useGetCMSResourceURL } from 'js/hooks/useGetCMSResourceURL';
import { IGamesConfiguration } from 'js/hooks/games/hooks/useGetGamesConfiguration/types';
import { parseGamesConfigurationResponse } from 'js/hooks/games/hooks/useGetGamesConfiguration/helpers';
import { useAppSelector, useDeviceSelector, useGameConfigurationSelector, useUserSelector } from 'js/store/hooks';

export const useGetGamesConfiguration = () => {
    const lang = useUserSelector(prop('lang'));
    const getCMSResourceURL = useGetCMSResourceURL();
    const requestStamp = useAppSelector(prop('requestStamp'));
    const isGamesLoaded = useGameConfigurationSelector(prop('isGamesLoaded'));
    const { formFactor, os, browserName } = useDeviceSelector(pick(['formFactor', 'os', 'browserName']));
    const gamesData = useGameConfigurationSelector(pick([
        'games', 'gameTypes',
        'basicGameTileLayout', 'liveGameTileLayout',
        'basicGameInfoLayout', 'liveGameInfoLayout'
    ]));

    return useCallback(async (disableCache?: boolean): Promise<IGamesConfiguration | undefined> => {
        if (!lang) {
            return;
        }

        if (isGamesLoaded && !disableCache) {
            return gamesData as IGamesConfiguration;
        }

        const requestURL = getCMSResourceURL(`/initialResources/gamesConfiguration/${lang}`, { t: requestStamp });
        const response = await get(requestURL);

        return parseGamesConfigurationResponse({
            os,
            response,
            formFactor,
            browserName
        });
    }, [isGamesLoaded, gamesData, lang, requestStamp, getCMSResourceURL, formFactor, os, browserName]);
};
